import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Card, Empty, Input, Skeleton, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { PaymentMethod, ReceiptRow } from '@velora/contracts';
import { productApi } from '../../api';

/**
 * Accounts desk — Receipts.
 * Today's collections + date-range filter. Serves the day-close use case:
 * total by method, receipt count, easy reconciliation.
 */
export function AccountsReceiptsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());

  const q = useQuery({
    queryKey: ['receipts', institution, from, to],
    queryFn: () => productApi.listReceipts(institution, { from, to }),
    enabled: !!institution,
  });

  const rows = q.data?.rows ?? [];
  const byMethod = rows.reduce<Record<string, number>>((a, r) => {
    a[r.method] = (a[r.method] ?? 0) + r.amount_paise;
    return a;
  }, {});

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Receipts</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          {rows.length} receipt{rows.length === 1 ? '' : 's'} · ₹{paise(q.data?.total_paise ?? 0)} collected.
        </Typography.Text>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          size="small" value={from} onChange={(e) => setFrom(e.target.value)}
          style={{ width: 150, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} placeholder="from yyyy-mm-dd"
        />
        <Input
          size="small" value={to} onChange={(e) => setTo(e.target.value)}
          style={{ width: 150, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} placeholder="to yyyy-mm-dd"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8 }}>
        {(['cash', 'upi', 'card', 'bank_transfer', 'cheque'] as PaymentMethod[]).map((m) => (
          <Card key={m} size="small"><Statistic title={m.replace(/_/g, ' ')} value={`₹${paise(byMethod[m] ?? 0)}`} /></Card>
        ))}
      </div>

      <Card size="small">
        {q.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !q.isLoading && (
          <Empty description="No receipts in this range." style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<ReceiptRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Receipt no.', dataIndex: 'receipt_number', key: 'n', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Student', key: 's', render: (_, r) => <><b>{r.student_name}</b> <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {r.admission_number}</Typography.Text></> },
              { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => <b>₹{paise(v)}</b> },
              { title: 'Method', dataIndex: 'method', key: 'm', render: (v: PaymentMethod) => <Tag>{v}</Tag> },
              { title: 'Date', dataIndex: 'received_on', key: 'd', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

function today(): string { return new Date().toISOString().slice(0, 10); }
function paise(n: number): string { return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
